import React, { useRef, useEffect } from 'react'
import { ReactTable } from '../reactTable'
import { ReactTableProps } from '../reactTable/interface'
import { CursorPagination } from '../../../../types'
import LoaderGif from 'uiComponents/loaders/loader.gif'
import BaseKnowledgeLink from 'uiComponents/typography/BaseKnowledgeLink'
import './infiniteTable.scss'
import { useTranslation } from 'translations'

export interface InfiniteTableProps<D extends object> extends Omit<ReactTableProps<D>, 'pagination' | 'loadData'> {
    loading?: boolean
    loadingMore: boolean
    pagination: CursorPagination
    onLoadMore: () => void
    loadingMessage?: string
    knowledgeLink?: string
    toolbar?: React.ReactNode
}

export const InfiniteTable = <D extends object>({
    data,
    columns,
    loadingMore,
    pagination,
    onLoadMore,
    loadingMessage,
    knowledgeLink,
    loading,
    toolbar,
    ...restProps
}: InfiniteTableProps<D>) => {
    const { t } = useTranslation()
    const observerTarget = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries
                if (entry.isIntersecting && pagination.next && !loadingMore) {
                    onLoadMore()
                }
            },
            {
                root: null,
                rootMargin: '100px',
                threshold: 0.1,
            },
        )

        const currentTarget = observerTarget.current
        if (currentTarget) {
            observer.observe(currentTarget)
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget)
            }
            observer.disconnect()
        }
    }, [pagination.next, loadingMore, onLoadMore])

    return (
        <div className="infinite-table-wrapper">
            <div className="infinite-table-card">
                {toolbar}
                <ReactTable {...restProps} data={data} columns={columns} pagination={pagination} loading={loading} />
            </div>
            {knowledgeLink && <BaseKnowledgeLink link={knowledgeLink} />}
            <div ref={observerTarget} className="loader-trigger">
                {!!data.length && (
                    <div className="loading-indicator">
                        {loadingMore ? (
                            <>
                                <img className="spinner" src={LoaderGif} />
                                {loadingMessage ?? t('uiComponents.table.loadingMore')}
                            </>
                        ) : !pagination.next ? (
                            t('uiComponents.table.noMoreResults')
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    )
}
